import { getBlockComponent } from "@/common/blocks/registry";
import type { TableContent } from "@/types/report";

interface Props {
  content: TableContent;
}

/**
 * Normalize LLM-generated table data to the format our components expect.
 * LLMs often return simple arrays instead of the structured objects components need.
 */
function normalizeTableData(blockType: string, data: any): any {
  if (!data) return data;

  // Data grid: string[] columns + array-of-arrays rows -> object columns + object rows
  if (blockType === 'data-grid' && Array.isArray(data.columns) && data.columns.length > 0 && typeof data.columns[0] === 'string') {
    const colStrings = data.columns as string[];
    const columns = colStrings.map((label: string, i: number) => ({ key: `col${i}`, label }));
    const rows = (data.rows || []).map((row: any) => {
      if (Array.isArray(row)) {
        const obj: Record<string, any> = {};
        row.forEach((val: any, i: number) => { obj[`col${i}`] = val; });
        return obj;
      }
      return row;
    });
    return { ...data, columns, rows };
  }

  // Key-value list: array-of-arrays rows -> items array
  if (blockType === 'key-value-list' && Array.isArray(data.rows) && !data.items) {
    if (data.rows.length > 0 && Array.isArray(data.rows[0])) {
      return {
        items: data.rows.map((row: any[]) => ({ label: row[0], value: row[1] })),
      };
    }
  }

  // Table: string[] columns + array-of-arrays rows -> header + rows
  if (blockType === 'table' && Array.isArray(data.columns) && data.columns.length > 0 && typeof data.columns[0] === 'string') {
    return {
      header: data.columns,
      rows: (data.rows || []).map((row: any) => Array.isArray(row) ? row : Object.values(row)),
    };
  }

  return data;
}

export function TableSection({ content }: Props) {
  const Component = getBlockComponent(content.blockType);

  if (!Component) {
    return (
      <div className="report-table-section">
        <p>Unsupported table type: {content.blockType}</p>
      </div>
    );
  }

  const normalizedData = normalizeTableData(content.blockType, content.blockData);

  return (
    <div className="report-table-section">
      <Component data={normalizedData} />
      {content.caption && <div className="caption">{content.caption}</div>}
    </div>
  );
}
